import cx from "classnames";
import React from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { LockSolid, WarningSolid } from "@gaming/xds-react-js-icons";

import {
  isGameConceptHardRejected,
  isGameConceptSoftRejected,
} from "../../helpers/gameConceptHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import {
  CrmGameConceptStatus,
  CrmGameConceptSubStatus,
  CurrentGameConcept,
} from "../../models/crmStatus";
import * as styles from "./GameConceptDisplayMessage.module.css";

type GameConceptDisplayMessageProps = {
  currentGameConcept: CurrentGameConcept | undefined;
  columnAligned?: boolean;
};

export const GameConceptDisplayMessage = ({
  currentGameConcept,
  columnAligned,
}: GameConceptDisplayMessageProps) => {
  const [
    warning,
    needMoreInfoBuildLinksMessage,
    needMoreInfoVideoLinksMessage,
    needMoreInfoBuildVideoLinksMessage,
    needMoreInfoReviewTooEarlyMessage,
    needMoreInfoSimShipMessage,
    needMoreInformationWarningDescription,
    softRejectedGameConceptMessage,
    infoBoxApplicationRejectedMessage,
    infoBoxApplicationRejectedSubtitle,
  ] = useStrings(
    "warning",
    "needMoreInfoBuildLinksMessage",
    "needMoreInfoVideoLinksMessage",
    "needMoreInfoBuildVideoLinksMessage",
    "needMoreInfoReviewTooEarlyMessage",
    "needMoreInfoSimShipMessage",
    "needMoreInformationWarningDescription",
    "softRejectedGameConceptMessage",
    "infoBoxApplicationRejectedMessage",
    "infoBoxApplicationRejectedSubtitle"
  );

  if (!currentGameConcept) {
    return null;
  }

  const crmGameConceptStatus = currentGameConcept.crmGameConceptStatus;
  const crmGameConceptSubStatus = currentGameConcept.crmGameConceptSubStatus;

  // Hard rejected is terminal: show a locked, error-styled banner in place of any guidance.
  if (isGameConceptHardRejected(crmGameConceptStatus, crmGameConceptSubStatus)) {
    return (
      <div
        className={cx(styles.banner, styles.bannerError, columnAligned && styles.columnAlign)}
        role="alert"
      >
        <span className={cx(styles.iconWrapper, styles.iconWrapperError)}>
          <Icon aria-hidden="true" icon={LockSolid} iconSize={IconSize.Custom} />
        </span>
        <div className={styles.textWrapper}>
          <p className={styles.title}>{infoBoxApplicationRejectedMessage}</p>
          <p className={styles.description}>{infoBoxApplicationRejectedSubtitle}</p>
        </div>
      </div>
    );
  }

  // Only "Need more information" and soft-rejected concepts get an actionable warning banner.
  if (
    crmGameConceptStatus !== CrmGameConceptStatus.MoreInfoNeeded &&
    !isGameConceptSoftRejected(crmGameConceptStatus, crmGameConceptSubStatus)
  ) {
    return null;
  }

  let description: string | undefined;
  switch (crmGameConceptSubStatus) {
    case CrmGameConceptSubStatus.NeedBuildLink:
      description = needMoreInfoBuildLinksMessage;
      break;
    case CrmGameConceptSubStatus.NeedVideoLink:
      description = needMoreInfoVideoLinksMessage;
      break;
    case CrmGameConceptSubStatus.NeedBuildAndVideoLink:
      description = needMoreInfoBuildVideoLinksMessage;
      break;
    case CrmGameConceptSubStatus.ReviewTooEarly:
      description = needMoreInfoReviewTooEarlyMessage;
      break;
    case CrmGameConceptSubStatus.ReviewSimShip:
      description = needMoreInfoSimShipMessage;
      break;
    case CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken:
    case CrmGameConceptSubStatus.RejectedSoftGeneral:
      description = softRejectedGameConceptMessage;
      break;
    case CrmGameConceptSubStatus.Unset:
    default:
      description =
        currentGameConcept.crmGameConceptApprovalComments ?? needMoreInformationWarningDescription;
      break;
  }

  return (
    <div
      className={cx(styles.banner, styles.bannerWarning, columnAligned && styles.columnAlign)}
      role="status"
    >
      <span className={cx(styles.iconWrapper, styles.iconWrapperWarning)}>
        <Icon aria-hidden="true" icon={WarningSolid} iconSize={IconSize.Custom} />
      </span>
      <div className={styles.textWrapper}>
        <p className={styles.title}>{warning}</p>
        <p className={styles.description}>{description}</p>
      </div>
    </div>
  );
};
